import {useCallback, useEffect, useState} from "react";
import {AlertTriangle, MessageSquare} from "lucide-react";
import {
  createAdminProductReview,
  deleteAdminProductReview,
  listAdminProductReviews,
  listAdminProducts,
  updateAdminProductReview,
  type AdminProductReview
} from "@zaaaz/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Textarea} from "@/components/ui/textarea";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs, AdminToggleField, AdminRowTrailing} from "./ui";

const emptyForm = {status: 0, productId: 0, rating: 4, nume: "", email: "", telefon: "", mesaj: ""};

export function ProductReviewsView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("produse");
  const [items, setItems] = useState<AdminProductReview[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [productOpts, setProductOpts] = useState<{id: number; label: string}[]>([]);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminProductReviews({page, pageSize: 20, search: search || undefined})
      .then((r) => { setItems(r.items); setTotalPages(r.totalPages); })
      .catch((e: Error) => setErr(e.message));
  }, [page, search]);
  useEffect(() => { load(); }, [page]);
  useEffect(() => {
    listAdminProducts({page: 1, pageSize: 100}).then((r) => {
      setProductOpts(r.items.map((p) => ({id: p.id, label: `${p.cod} ${p.nume}`})));
    }).catch(() => {});
  }, []);
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(r: AdminProductReview) {
    setOpenId(r.id);
    setForm({status: r.status, productId: r.productId, rating: r.rating, nume: r.nume, email: r.email, telefon: r.telefon, mesaj: r.mesaj});
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          await createAdminProductReview(form);
          adminToast.success("Review added.");
        } else if(typeof openId === "number") {
          await updateAdminProductReview(openId, form);
          adminToast.success("Review saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete review?", async () => {
      await deleteAdminProductReview(id);
      closeModal(false);
      load();
      adminToast.success("Review deleted.");
    });
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.produse.title}
      description="Moderate customer reviews"
      breadcrumbs={[
        {label: ADMIN_SECTION_NAV.produse.title, href: "/vadmin/produse"},
        {label: "Reviews"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add review" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="produse" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(r) => r.id}
        getTitle={(r) => r.nume}
        getSubtitle={(r) => r.productName}
        getMeta={(r) => (
          <>
            {r.status === 0 ? <AdminBadge variant="danger"><AlertTriangle className="h-3 w-3" /></AdminBadge> : null}
            <AdminBadge variant="secondary">{r.dateLabel}</AdminBadge>
          </>
        )}
        getSearchText={(r) => adminSearchText(r.id, r.nume, r.email, r.telefon, r.mesaj, r.productName, r.productId, r.rating, r.dateLabel, r.status)}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No reviews"
        emptyDescription="Add a new review or adjust filters."
        searchPlaceholder="Search name, email, mesaj, produs..."
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={() => { setPage(1); load(); }}
        clientFilter={false}
        getRowActions={(r) => (
          <AdminRowTrailing onDelete={() => removeItem(r.id)} deleteLabel="Delete review" />
        )}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New review" : "Edit review"}
        icon={MessageSquare}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          {openId === "new" ? (
            <>
              <AdminField label="Name">
                <Input value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} placeholder="Name" />
              </AdminField>
              <AdminField label="Product">
                <Select value={String(form.productId)} onValueChange={(v) => setForm({...form, productId: Number(v)})}>
                  <SelectTrigger><SelectValue placeholder="Product" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Produs</SelectItem>
                    {productOpts.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </AdminField>
            </>
          ) : null}
          <AdminToggleField
            title="Approved"
            description="Approved reviews are visible on the product page."
            checked={form.status === 1}
            onCheckedChange={(v) => setForm({...form, status: v ? 1 : 0})}
          />
          <AdminField label="Rating">
            <Select value={String(form.rating)} onValueChange={(v) => setForm({...form, rating: Number(v)})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </AdminField>
          <AdminField label="Message">
            <Textarea value={form.mesaj} onChange={(e) => setForm({...form, mesaj: e.target.value})} rows={4} />
          </AdminField>
        </div>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
